/* global Word*/

import { findPartialStringIndexWrapper, findStringMatchKMP, searchText } from "./searchText";
import { type ChangeObject, diffWords } from "diff";

const locateInDoc = async (start, end, reference, externalContext = null) => {
	return Word.run(async (context) => {
		try {
			if (externalContext) {
				context = externalContext;
			}
			const paragraphs = context.document.body.paragraphs;
			paragraphs.load("items/text");
			await context.sync();

			const endIndex = Object.keys(end).length == 0 ? start.object.index + 1 : end.object.index + 1;

			const paragraphSlicedRange = paragraphs.items.slice(start.object.index, endIndex);

			let initialTextChunk;
			let endTextChunk;
			let initialParagraphSubStringMatch;
			let endParagraphSubStringMatch;
			if (paragraphSlicedRange.length == 1) {
				const unsanitizedIndex = findOriginalIndex(paragraphSlicedRange[0].text, start.initialString);
				console.log(unsanitizedIndex, start, end);

				initialTextChunk = paragraphSlicedRange[0].text.slice(unsanitizedIndex, unsanitizedIndex + reference.length);

				// even if the string is less than 250 and startText and endText overlaps it wont affect anything
				// the range function below creates the same range
				const startText = initialTextChunk.slice(0, 250);
				const endText = initialTextChunk.slice(-250);

				initialParagraphSubStringMatch = await searchString(context, paragraphSlicedRange[0], startText);
				endParagraphSubStringMatch = await searchString(context, paragraphSlicedRange[0], endText);
			} else {
				const unsanitizedIndex = findOriginalIndex(paragraphSlicedRange[0].text, start.initialString);
				initialTextChunk = paragraphSlicedRange[0].text.slice(unsanitizedIndex, unsanitizedIndex + reference.length);
				initialTextChunk = initialTextChunk.slice(0, 250);
				const endIndex = end.endString == 0 ? reference.length : end.endString;
				const unsanitizedIndex2 = findOriginalIndex(
					paragraphSlicedRange[paragraphSlicedRange.length - 1].text,
					endIndex
				);
				endTextChunk = paragraphSlicedRange[paragraphSlicedRange.length - 1].text.slice(0, unsanitizedIndex2);

				// did it this way here because i want to get last 250 chars from this substring
				endTextChunk = endTextChunk.slice(-250);
				initialParagraphSubStringMatch = await searchString(context, paragraphSlicedRange[0], initialTextChunk);

				endParagraphSubStringMatch = await searchString(
					context,
					paragraphSlicedRange[paragraphSlicedRange.length - 1],
					endTextChunk
				);
			}

			const initialParagraph = initialParagraphSubStringMatch.items[0].getRange("Start");

			const endParapgraph = endParagraphSubStringMatch.items[0].getRange("End");

			const fullRange: Word.Range = initialParagraph.expandTo(endParapgraph);

			fullRange.select();
			return fullRange;
		} catch (err) {
			console.log(err);
		}
	});
};

const addMissing = (referenceToAdd, headingToAdd) => {
	return Word.run(async (context) => {
		try {
			const paragraphs = context.document.body.paragraphs;
			paragraphs.load("items/text");
			await context.sync();

			const paragraphInstance = paragraphs.items[paragraphs.items.length - 1];

			// Insert the text and get a range to it

			const heading = paragraphInstance.insertParagraph(headingToAdd, "After");
			heading.font.bold = true;
			heading.insertBreak("Line", "After");
			await context.sync();

			const newPara = heading.insertParagraph(" " + referenceToAdd, "After");

			// Queue a selection of just the inserted text
			newPara.select();
			await context.sync();
			return true;
		} catch (err) {
			console.log(err);
			return false;
		}
	});
};

interface IEditDocParams {
	start: Object;
	end: Object;
	reference: string;
	html_version: string;
	comment: string;
	insertComment: boolean;
}

/**
 * Applies word-wise changes to a range in the document using the diff array
 */
async function applyWordWiseChanges(
	context: Word.RequestContext,
	range: Word.Range,
	diff: ChangeObject<string>[]
): Promise<void> {
	const doc = context.document;

	// Turn OFF track changes and clear the range
	doc.changeTrackingMode = Word.ChangeTrackingMode.off;
	range.insertText("", Word.InsertLocation.replace);
	await context.sync();

	doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

	// Start inserting at the cleared range
	let insertRange = range;

	for (const part of diff) {
		if (part.removed) {
			doc.changeTrackingMode = Word.ChangeTrackingMode.off;
			const tempRange = insertRange.insertText(part.value, Word.InsertLocation.end); // returns Range
			await context.sync();

			// Turn tracking ON, then delete the inserted range → tracked deletion
			doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
			tempRange.delete(); // no arguments here
			await context.sync();
		} else if (part.added) {
			// Insert added text at the END
			insertRange.insertText(part.value, Word.InsertLocation.end);
			insertRange = insertRange.getRange(Word.InsertLocation.end);
		} else {
			// Insert unchanged text normally (track changes off to avoid redlines)
			doc.changeTrackingMode = Word.ChangeTrackingMode.off;
			insertRange.insertText(part.value, Word.InsertLocation.end);
			insertRange = insertRange.getRange(Word.InsertLocation.end);
			await context.sync();
			doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
		}
	}

	await context.sync();
}

const editDoc = async ({ start, end, reference, html_version, comment, insertComment }: IEditDocParams) => {
	try {
		return Word.run(async (context) => {
			try {
				const paragraphs = context.document.body.paragraphs;
				paragraphs.load("items/text");
				await context.sync();

				const fullRange = await locateInDoc(start, end, reference, context);

				// Load the current text content
				fullRange.load("text");
				await context.sync();
				const currentText = fullRange.text;

				// Remove any existing comments for this change
				const comments = fullRange.getComments();
				comments.load("items");
				await context.sync();
				comments.items.forEach((c) => {
					if (c.content === comment) {
						c.delete();
					}
				});
				await context.sync();

				// Save font settings
				fullRange.font.load(["name", "size"]);
				await context.sync();
				const fontName = fullRange.font.name;
				const fontSize = fullRange.font.size;

				// Generate word-wise diff
				const diff = diffWords(currentText, html_version);

				// Apply word-wise changes using the diff
				await applyWordWiseChanges(context, fullRange, diff);

				// Disable track changes
				// context.document.changeTrackingMode = Word.ChangeTrackingMode.off;

				// Apply original formatting to the changed text
				const changedRange = fullRange.getRange();
				changedRange.font.load(["name", "size"]);
				changedRange.font.name = fontName;
				if (fontSize) {
					changedRange.font.size = fontSize;
				}
				await context.sync();

				// Add comment if needed
				if (insertComment && comment) {
					changedRange.insertComment(comment);
					await context.sync();
				}

				return true;
			} catch (err) {
				console.error("Caught RichApi error:", err);
				if (err.debugInfo) {
					console.error("Debug info:", err.debugInfo);
				}
				throw err; // Re-throw to be caught by the outer try-catch
			}
		});
	} catch (err) {
		console.error("Error in editDoc:", err);
		return false;
	}
};

const searchString = async (context, searchInstance, reference) => {
	console.log({ len: reference.length, reference });

	// these characters are need to be replaced because this character is added when
	// we add a comment add in adds this character as an anchor to show the location of comment
	// but it causes issues during search so need to remove it before performing the search
	searchInstance._Te = searchInstance._Te.replace(/\x05/g, "");
	reference = reference.replace(/\x05/g, "");
	const searchResults = searchInstance.search(reference, {
		matchCase: false,
		matchWholeWord: false,
		ignoreSpace: true,
		ignorePunct: false,
		matchPrefix: false,
		matchSuffix: false,
		matchWildcards: false,
	});
	searchResults.load("items");
	await context.sync();

	return searchResults;
};

/**
 * Creates a structured map of the document's paragraph content to support efficient, formatting-insensitive search.
 *
 * Why use paragraphs?
 * Word documents may contain newline characters (`\n`) or tabs (`\t`) that interfere with accurate text searches.
 * Using the document's `.text` property can introduce inconsistencies in spacing and formatting. Instead,
 * this function works at the paragraph level to reliably extract and normalize text.
 *
 * It builds a `docMap` object that contains:
 * - `string`: A single concatenated string formed by removing spaces and escape sequences from all paragraphs.
 * - `index_map`: A mapping from character positions in the `string` to the corresponding paragraph index,
 *    its original (normalized) text, and the start index of the previous paragraph.
 * - `lastIndex`: The end index of the final paragraph in the aggregated string. This is used as an optimization —
 *    if the index returned by Boyer-Moore exceeds `lastIndex`, we can immediately return the last known valid index
 *    without performing a binary search over `index_map` keys.
 *
 * This map enables advanced search algorithms (e.g., Boyer-Moore) to work on the clean, normalized string
 * and later map back the result index to the corresponding paragraph in the original document.
 *
 * @returns {Promise<{ string: string, index_map: Record<number, { index: number, string: string, lastParentIndex: number }>, lastIndex: number }>}
 *          A Promise that resolves to a `docMap` used for search and reverse-mapping.
 *
 * **************** FOR FUTURE **************
 * If this functions starts becoming performance bottleneck because we are creating this map everytime someone
 * interacts with the document then we will have to use piece Tables Data structure (used in making text editors)
 * to track the changes in the document ourselves instead of making this map again after every change, but do this onlt if
 * there is a performance problem
 */

const createDocMap = () => {
	return Word.run(async (context) => {
		const paragraphs = context.document.body.paragraphs;
		paragraphs.load("items");
		await context.sync();

		const docMap = { string: "", index_map: {}, lastIndex: 0 };

		let lastIndex = 0;
		let lastParentIndex = 0;
		paragraphs.items.forEach((para, index) => {
			/*
      Added this conditional here because sometimes a para is simply an empty string like "" and 
      when this is added and we get an index in between a string lets say we got an index 3657
      which is actually an index in between our hashmap keys so we have to find the nearest key index, but
      because there was an empty string there is no increment in length and thats why the first value which got an index lets say 3555
      is replaced with the next empty string because hashmap doesnot allows duplicate keys so because there was no incremenet in length
      there was no increase in index and hence it got the same 3555 again and it replaced our old element which had its own value.
      */
			if (para.text.trim().length > 0) {
				const finalText = removeNonAlphanumeric(para.text);
				// there is a reason we have put parentIndex after docMap.string update
				// because we have to add the parentIndedx with updated number to make a key out of it for our
				// hash map
				lastParentIndex = lastIndex;
				const parentIndex = docMap.string.length;
				docMap.string += finalText;
				docMap.index_map[parentIndex] = {
					index,
					string: finalText,
					lastParentIndex,
					cleanedStringLength: finalText.length,
				};
				lastIndex = parentIndex;
			}
		});

		docMap.lastIndex = lastIndex;
		return docMap;
	});
};

/**
 * Performs a custom search and optional edit or location operation on a Word document based on a reference string.
 *
 * Why recreate the `docMap` every time this function runs? Why not cache it for the next time?
 * The structure of a Word document can change dynamically — especially when users accept/reject suggestions or make edits
 * between function calls. Because `docMap` depends on the exact paragraph structure and text, it must be regenerated
 * on each invocation to ensure accuracy.
 *
 * How the function works:
 * 1. **Create `docMap`:** Generates a normalized, space-free string of the document along with a mapping (`index_map`)
 *    that connects character positions back to paragraph indices. This allows search to be performed without being
 *    affected by formatting or whitespace irregularities.
 *
 * 2. **Preprocess `reference`:** All whitespace is stripped from the input reference string to match the cleaned `docMap.string`.
 *
 * 3. **Run Boyer-Moore Search:** The `searchText` function (based on Boyer-Moore) searches the cleaned document string
 *    for the reference and returns an index (e.g., 900). However, this index may not align directly with a paragraph boundary.
 *
 * 4. **Find Start Position:**
 *    - The raw index is used to retrieve the closest paragraph start via `findPartialStringIndexWrapper`.
 *    - The actual start location of the `reference` within that paragraph’s full text is then found using **KMP**.
 *      → KMP (Knuth-Morris-Pratt) helps find the exact character index within the paragraph text — even if only a portion
 *        of the `reference` is present in this paragraph. It accounts for partial matches at boundaries and avoids
 *        backtracking unlike naïve string matching.
 *
 * 5. **Find End Position (if needed):**
 *    - For actions other than `"locate"`, the same logic is repeated at the end of the reference.
 *    - The offset is added to the original index to find the end position, which is then reverse-mapped via the doc map.
 *    - KMP is used again to find where the last part of the reference ends within the ending paragraph’s text.
 *
 * 6. **Act on Result:**
 *    - If the action is `"locate"`, it visually highlights the relevant portion in the document.
 *    - Otherwise, the function performs an edit using `editDoc()` (e.g., replacement or commenting).
 *
 * Why use this custom search?
 * We use this custom search to efficiently find the exact range of paragraphs that contain the desired text (which can be efficient),
 * rather than searching the entire document text with the Word API. A normal search, even when splitting the text
 * into 250-character chunks, can still fail due to the presence of newline characters (`\n`). If even a single newline
 * exists in the text, the standard search will break and may not return the correct result. This approach ensures that
 * we can find the text more reliably and accurately by directly working with paragraph-level indexing.
 *
 * @param {string} action - The operation to perform ("locate", "replace", "comment", etc.)
 * @param {string} reference - The string to search for in the document.
 * @param {string|null} new_version - The replacement string, if editing.
 * @param {string} comment - Any associated comment to insert.
 */
interface ICustomDocSearch {
	action: string;
	reference: string;
	comment?: string;
	html_version?: string;
	insertComment?: boolean;
}

async function customDocumentSearch({ action, reference, comment, html_version, insertComment }: ICustomDocSearch) {
	try {
		const docMap = await createDocMap();

		// removing any non alpha numeric items from the string because there are some characters in word document which we cant read
		// and they cause issues in string match, so its better to remove them altogether
		const cleanedReference = removeNonAlphanumeric(reference);

		// returns an index of substring from the concatenated string of the whole document
		// which is then used to find which hashmap key from docMap contains this substring
		// so that we can use that to find the paragraph instance because they are stacked in that order
		let response = searchText(docMap.string, cleanedReference);

		if (response.length === 0) response = await longestConsecutiveCluster(docMap.string, reference);

		// response = await longestConsecutiveCluster(docMap.string, reference);

		// takes the response from searchText and finds the index in docMap which contains the substring
		const initialIndexObject = findPartialStringIndexWrapper(response[0], docMap);

		let initialIndex = findStringMatchKMP(initialIndexObject.string, cleanedReference);

		let fullMatch = false;

		let end = {};

		if (typeof initialIndex == "object") {
			fullMatch = true;
			initialIndex = initialIndex.index;
		}

		// initialIndex = response[0] - initialIndexObject.currentIndex;

		if (!fullMatch) {
			const overlap = substringOverlapInChunk(
				response[0],
				initialIndexObject.cleanedStringLength,
				Number(initialIndexObject.currentIndex)
			);
			const endParaIndex =
				Number(initialIndexObject.currentIndex) +
				initialIndexObject.cleanedStringLength +
				cleanedReference.length -
				overlap -
				1;
			const finalIndexObject = findPartialStringIndexWrapper(endParaIndex, docMap);
			// let endIndex = findStringMatchKMP(finalIndexObject.string, cleanedReference, "prefixSuffix");
			// console.log({
			// 	initialIndex,
			// 	docMap,
			// 	finalIndexObject,
			// 	initialIndexObject,
			// 	cleanedReferenceLength: cleanedReference.length,
			// 	overlap,
			// 	response,
			// 	cleanedReference,
			// });

			// Handle case where match result is an object
			// if (typeof endIndex === "object") {
			// 	endIndex = endIndex.index;
			// }
			// If found in either attempt, set `end`
			// if (endIndex !== -1) {
			end = {
				object: finalIndexObject,
				endString: finalIndexObject.cleanedStringLength,
			};
			// }
		}

		const start = {
			object: initialIndexObject,
			initialString: initialIndex,
		};

		if (action == "locate") {
			await locateInDoc(start, end, reference);
			return true;
		} else {
			const response = await editDoc({ start, end, reference, comment, html_version, insertComment });
			return response;
		}
	} catch (err) {
		console.error(err);
		return false;
	}
}

async function longestConsecutiveCluster(docMap: string, reference: string): Promise<[number]> {
	let sourceText = "";
	await Word.run(async (context) => {
		const paragraphs = context.document.body.paragraphs;
		paragraphs.load("items");
		await context.sync();
		sourceText = paragraphs.items.map((para) => para.text).join(" ");
	});

	const sourceWords: string[] = sourceText.split(/\s+/);
	const patternWords: string[] = reference.split(/\s+/);

	const sourceMap: Record<string, Set<number>> = {};

	sourceWords.forEach((word, i) => {
		if (!sourceMap[word]) {
			sourceMap[word] = new Set<number>();
		}
		sourceMap[word].add(i);
	});

	let longestChain: number[] = [];
	let initialIndex: number = 0;

	// Step 3: iterate each pattern word as potential start
	let i = 0;
	while (i < patternWords.length) {
		const word = patternWords[i];
		const positions: Set<number> = sourceMap[word] || new Set<number>();

		if (positions.size === 0) {
			i++;
			continue; // skip missing word
		}

		let bestBreakPoint = i;

		positions.forEach((startPos) => {
			const currentChain: number[] = [startPos];
			let lastPos = startPos;
			let patternIdx = i + 1;

			// Extend chain
			while (patternIdx < patternWords.length) {
				const nextWord = patternWords[patternIdx];
				const nextPositions = sourceMap[nextWord] || new Set<number>();

				if (nextPositions.has(lastPos + 1)) {
					lastPos += 1;
					currentChain.push(lastPos);
					patternIdx += 1;
				} else {
					break; // break chain
				}
			}

			// Update longest chain
			if (currentChain.length > longestChain.length) {
				longestChain = currentChain;
				initialIndex = i;
			}

			// Track how far we reached
			if (patternIdx > bestBreakPoint) {
				bestBreakPoint = patternIdx;
			}
		});

		// Jump ahead
		i = bestBreakPoint > i ? bestBreakPoint : i + 1;
	}

	let wordLength = 0;

	for (let i = 0; i < initialIndex; i++) {
		if (sourceMap[patternWords[i]]) wordLength += patternWords[i].length;
	}

	// Step 4: extract matched text
	if (longestChain.length > 0) {
		const start = longestChain[0];
		const end = longestChain[longestChain.length - 1] + 1;
		const foundText = sourceWords.slice(start, end).join(" ");
		const sourceIndex = sourceText.indexOf(foundText);
		const sanitizedIndex = findSanitizedIndex(sourceText, docMap, sourceIndex);
		// for (let i = initialIndex - 1; i >= 0; i--) {
		// 	const word = patternWords[i];
		// 	const positions = sourceMap[patternWords[i]];

		// 	console.log({ positions, sourceIndex, word, len: word.length, initialIndex });

		// 	if (positions) {
		// 		if (positions.has(sourceIndex - word.length - 1)) {
		// 			wordLength += patternWords[i].length;
		// 		}
		// 	}
		// }
		return [sanitizedIndex - wordLength];
	}

	return [-1];
}

function findSanitizedIndex(unsanitized: string, sanitized: string, originalIndex: number): number {
	let sanitizedIndex = 0;

	for (let i = 0; i < unsanitized.length; i++) {
		const char = unsanitized[i];

		// If we reached the original index, return the current sanitized index
		if (i === originalIndex) {
			return sanitizedIndex;
		}

		// Only increment sanitizedIndex for chars that survive sanitization
		if (sanitized[sanitizedIndex] === char) {
			sanitizedIndex++;
		}
	}

	return -1; // fallback
}

/**
 * Calculates how many characters of a substring fall within this chunk.
 *
 * @param {number} substringStart - The offset where the substring begins (relative to full document).
 * @param {string} chunk - The current string chunk (e.g. an element from your array of strings).
 * @param {number} chunkStart - The offset where this chunk begins in the full document.
 * @returns {number} - Number of characters from the substring that fall into this chunk.
 */
function substringOverlapInChunk(substringStart, chunk, chunkStart) {
	const chunkEnd = chunkStart + chunk; // End position of this chunk in the document
	const overlapStart = Math.max(substringStart, chunkStart); // Where the substring actually begins in this chunk
	const overlapEnd = chunkEnd; // Substring always goes beyond this chunk, so it ends at chunk end
	const overlapLength = Math.max(0, overlapEnd - overlapStart); // Only count if they actually overlap
	return overlapLength;
}

function normalizeChars(text: string) {
	return text
		.replace(/[‘’‛]/g, "'") // curly/apostrophe-style single quotes
		.replace(/[“”]/g, '"') // curly-style double quotes
		.replace(/\t/g, ""); // remove all tab characters
}

// its very important that we are not preserving spaces in this
// if someone added a space after '-9' in this regex then the search algorithm wont work properly
// because sometimes the doc is uneven, it has some spaces which are not in our string, so we need to ignore them
const ALPHANUMERIC_WHITESPACE_REGEX = /[a-zA-Z0-9]/;

/**
 * Removes all characters from the input string that are not in the ASCII range 33–127.
 *
 * This function is designed to normalize strings by stripping out non-standard or hidden
 * Unicode characters (such as curly quotes, invisible formatting marks, and emojis) that
 * can appear in Word documents. These characters often do not reach the backend during
 * processing but are still present in the document, leading to mismatches when trying to
 * locate and highlight text (e.g., during the "Locate in Document" operation).
 *
 * By limiting characters to the printable ASCII range (excluding space), we ensure a
 * consistent comparison between backend-returned text and the document content.
 *
 * @param {string} str - The input string to be cleaned.
 * @returns {string} - A string containing only characters with ASCII codes between 33 and 127.
 */
function removeNonAlphanumeric(str) {
	return str
		.split("")
		.filter((char) => ALPHANUMERIC_WHITESPACE_REGEX.test(char))
		.join("")
		.replace(/\d+/g, "");
}
/**
 * Finds a pre-sanitized substring in a pre-sanitized string and maps its
 * index back to the original, non-sanitized string.
 *
 * This function is created because i remove all the spaces and alpha numeric characters from a string so that i can search it easily
 * because word document adds some characters that it should not which are not present in our string from backend
 * so i remove all those chars and keep a hash map of it so that once i get the index from my sanitized search
 * i can send it to this function and it can return me the adjusted number which will include the length of all the
 * spaces and alphanumeric chars as well
 *
 * @param originalString The string before any characters were removed.
 * @param sanitizedString The version of the string with only alphanumeric characters and spaces.
 * @param sanitizedSubstring The substring to search for (already sanitized).
 * @returns The starting index of the substring in the originalString, or -1 if not found.
 */
function findOriginalIndex(originalString: string, sanitizedIndex: number): number {
	// 1. Create the index map from the original string.
	const indexMap: number[] = [];

	for (let i = 0; i < originalString.length; i++) {
		const char = originalString[i];
		if (ALPHANUMERIC_WHITESPACE_REGEX.test(char)) {
			indexMap.push(i);
		}
	}

	// 3. If found, use the indexMap to find the original position.
	if (sanitizedIndex > -1) {
		// Ensure the map has an entry for the found index
		if (sanitizedIndex < indexMap.length) {
			return indexMap[sanitizedIndex];
		}
	}

	return -1; // Return -1 if not found or if there's a mismatch
}

export { customDocumentSearch, addMissing, normalizeChars };
